<script lang="ts">
  import { onMount } from "svelte";
  import type { Resolution } from "$lib/auth/guards";
  import type { RouteName } from "../../router.svelte";
  import type { Permission, PortalFeature } from "$lib/api/contract";
  import { router, link } from "../../router.svelte";
  import { session } from "$lib/stores/session.svelte";
  import { org } from "$lib/stores/org.svelte";
  import { plans } from "$lib/stores/plans.svelte";
  import { planDisplayName, shouldShowUpgradeBanner } from "$lib/billing/offers";
  import { prefs } from "$lib/stores/prefs.svelte";
  import {
    forgetUpgradeIntent,
    pollUpgradeFulfillment,
    recallUpgradeIntent,
    upgradeResultOutcome,
    type UpgradeResultOutcome
  } from "$lib/billing/upgrade-fulfillment";
  import { track } from "$lib/telemetry/emit";
  import { loadPage } from "$lib/release/loadPage";
  import { AppError } from "$lib/api/problem";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { can, hasFeature } from "$lib/auth/permissions";
  import { planBadgeLabel, orgSwitcherItems } from "$lib/domain/org";
  import { isManagementOnly, isUnlicensedMember } from "$lib/domain/access";
  import Banner from "$lib/components/Banner.svelte";
  import AppLogo from "$lib/components/AppLogo.svelte";
  import Menu from "$lib/components/Menu.svelte";
  import AccountMenu from "$lib/components/AccountMenu.svelte";
  import type { MenuItem } from "$lib/components/types";
  import Badge from "$lib/components/Badge.svelte";
  import Drawer from "$lib/components/Drawer.svelte";
  import PermissionDenied from "./PermissionDenied.svelte";
  import TierUpgradeBanner from "./TierUpgradeBanner.svelte";
  import OpenQuoteNotification from "./OpenQuoteNotification.svelte";

  interface Props {
    view: Extract<Resolution, { kind: "app" | "denied" }>;
  }
  const { view }: Props = $props();

  const orgId = $derived(view.orgId);
  const currentOrg = $derived(session.orgById(orgId));
  // Owner/admin without a seat: full management chrome, explicit "no product" notice.
  const managementOnly = $derived(isManagementOnly(currentOrg));
  const unboundOrganization = $derived(managementOnly && !hasFeature(currentOrg, "portal.overview"));
  // Unseated ordinary member: the shell renders only the unlicensed notice, and the
  // server has already stripped every other feature from their org summary.
  const unlicensedMember = $derived(isUnlicensedMember(currentOrg));
  const canManageBilling = $derived(can(currentOrg, "billing.manage"));
  let upgradeSnoozed = $state(isUpgradeSnoozed());
  const showUpgradeBanner = $derived(
    shouldShowUpgradeBanner({
      canManageBilling,
      snoozed: upgradeSnoozed,
      routeName: view.name,
      currentAccessLevel: plans.currentAccessLevel,
      currentPlan: plans.currentPlan,
      offers: plans.offers,
      licensedSeats: org.license?.licensedSeats ?? null
    })
  );

  // Entering an org: point stores at it, persist the pointer, load the entitlement
  // snapshot (plan context + shared mutation gating).
  $effect(() => {
    const id = orgId;
    const unlicensed = unlicensedMember;
    org.setOrg(id);
    prefs.setLastOrg(id);
    // An unseated ordinary member has no licence view at all — the dashboard would be
    // refused, so don't ask for it just to throw the error away.
    if (!unlicensed && hasFeature(currentOrg, "portal.overview")) void org.load(id);
  });

  $effect(() => {
    const id = orgId;
    const allowed = canManageBilling;
    plans.setOrg(allowed ? id : null);
    if (allowed) void plans.load(id);
  });

  function isUpgradeSnoozed(): boolean {
    try {
      const value = window.localStorage.getItem("epidev.upgradeBanner.snoozedUntil");
      return value !== null && Number(value) > Date.now();
    } catch {
      return false;
    }
  }

  function upgradedPlanLabel(plan: string | null): string {
    if (plan === "builders" || plan === "team" || plan === "pro") return planDisplayName(plan);
    return "your new plan";
  }

  // `billing.upgrade_result` is only emitted when this tab started the upgrade and still
  // remembers its target and mode; the fulfillment read cannot supply a truthful target for a
  // canceled or expired intent (TUFF_026). A pending outcome keeps the memory for a retry.
  function reportUpgradeResult(intentId: string, outcome: UpgradeResultOutcome): void {
    const remembered = recallUpgradeIntent(intentId);
    if (!remembered) return;
    track({ name: "billing.upgrade_result", targetPlan: remembered.targetPlan, mode: remembered.mode, outcome });
    if (outcome !== "pending") forgetUpgradeIntent();
  }

  async function finishUpgrade(intentId: string, claim: string, signal: AbortSignal): Promise<void> {
    try {
      const fulfillment = await pollUpgradeFulfillment({ orgId, intentId, claim, signal });
      reportUpgradeResult(intentId, upgradeResultOutcome(fulfillment));
      if (fulfillment?.status === "fulfilled") {
        await Promise.all([session.refresh(), org.load(orgId), plans.load(orgId)]);
        toasts.success(`Your account is now on ${upgradedPlanLabel(fulfillment.plan)}.`);
        return;
      }
      if (fulfillment?.status === "canceled") {
        toasts.info("Upgrade canceled. Your plan was not changed.");
        return;
      }
      if (fulfillment?.status === "expired") {
        toasts.info("This upgrade session expired. Start a new upgrade when you are ready.");
        return;
      }
      toasts.info("Your upgrade is still being finalized. Refresh shortly.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (error instanceof AppError) {
        reportUpgradeResult(intentId, "failure");
        toasts.error(
          "We could not confirm the upgrade. If Stripe accepted the change, your account will update shortly.",
          error.correlationId
        );
      }
    }
  }

  onMount(() => {
    upgradeSnoozed = isUpgradeSnoozed();
    const result = router.match.query.get("upgrade")?.toLowerCase();
    if (!result) return;

    const intentId = router.match.query.get("intent");
    const claim = router.match.query.get("claim");
    router.scrubQuery(["upgrade", "intent", "claim"]);

    if (result === "cancel" || result === "canceled" || result === "cancelled") {
      if (intentId) reportUpgradeResult(intentId, "canceled");
      toasts.info("Upgrade canceled. Your plan was not changed.");
      return;
    }
    if (result !== "success" || !intentId || !claim) return;

    const controller = new AbortController();
    void finishUpgrade(intentId, claim, controller.signal);
    return () => controller.abort();
  });

  interface NavItem {
    name: RouteName;
    label: string;
    href: string;
    feature: PortalFeature;
    /** Hide the item entirely when the capability is missing (e.g. owner-only billing). */
    permission?: Permission;
  }
  const navItems = $derived(
    (
      [
        { name: "overview", label: "Overview", href: `/orgs/${orgId}/overview`, feature: "portal.overview" },
        { name: "org.users", label: "Organization users", href: `/orgs/${orgId}/users`, feature: "portal.people", permission: "members.read" },
        { name: "plan.seats", label: "Plan & seats", href: `/orgs/${orgId}/plan`, feature: "portal.overview", permission: "members.read" },
        { name: "billing", label: "Billing", href: `/orgs/${orgId}/billing`, feature: "portal.billing", permission: "billing.manage" },
        { name: "access.policies", label: "Access policies", href: `/orgs/${orgId}/access-policies`, feature: "portal.access_policies", permission: "security.manage" },
        { name: "extension.policy", label: "Extension policy", href: `/orgs/${orgId}/extension-policy`, feature: "portal.extension_policy", permission: "security.manage" },
        { name: "sso", label: "SSO", href: `/orgs/${orgId}/sso`, feature: "portal.sso" },
        { name: "audit", label: "Audit", href: `/orgs/${orgId}/audit`, feature: "portal.audit" },
        { name: "settings", label: "Settings", href: `/orgs/${orgId}/settings`, feature: "portal.settings" }
      ] satisfies NavItem[]
    ).filter(
      (item) =>
        hasFeature(currentOrg, item.feature) && (!item.permission || can(currentOrg, item.permission))
    )
  );

  function isActive(name: RouteName): boolean {
    const current = router.match.name;
    if (name === "org.users") return current === "org.users" || current === "org.users.member";
    return current === name;
  }

  const orgMenuItems = $derived<MenuItem[]>(
    orgSwitcherItems(session.organizations, orgId, (id) => router.navigate(`/orgs/${id}/overview`))
  );

  // Restricted orgs are listed but never selectable: there is no data to show for them.
  const restrictedMenuItems = $derived<MenuItem[]>(
    session.restrictedOrganizations.map((entry) => ({
      id: `restricted-${entry.id}`,
      label: entry.name,
      description: "Network restricted",
      disabled: true,
      onSelect: () => {}
    }))
  );

  const switcherItems = $derived<MenuItem[]>([...orgMenuItems, ...restrictedMenuItems]);

  // A remembered admin path behind a capability the session no longer holds (a
  // revoked grant, or a feature turned off since) falls back to Customers rather than
  // landing the operator on a denial page.
  const adminViewPath = $derived.by(() => {
    const path = prefs.lastAdminPath;
    if (path === "/admin/organizations" && !session.canSupportOrganizations) return "/admin/entitlements";
    if (path === "/admin/discord" && !session.canManageDiscord) return "/admin/entitlements";
    return path;
  });
  const accountMenuItems = $derived<MenuItem[]>(
    session.canManageEntitlements
      ? [{
          id: "admin-view",
          label: "Switch to EpiDev admin",
          description: "EpiDev administration",
          onSelect: () => router.navigate(adminViewPath)
        }]
      : []
  );

  // Lazy page loading → production code split per page.
  function pageFor(name: RouteName) {
    switch (name) {
      case "overview":
        return { key: "overview", load: () => import("./Overview.svelte") };
      case "org.users":
      case "org.users.member":
        return { key: "org-users", load: () => import("./OrgUsers.svelte") };
      case "plan.seats":
        return { key: "plan-seats", load: () => import("./PlanSeats.svelte") };
      case "access.policies":
        return { key: "access-policies", load: () => import("./AccessPolicies.svelte") };
      case "extension.policy":
        return { key: "extension-policy", load: () => import("./ExtensionPolicy.svelte") };
      case "unlicensed":
        return { key: "unlicensed", load: () => import("./Unlicensed.svelte") };
      case "billing":
        return { key: "billing", load: () => import("./Billing.svelte") };
      case "org.quotes":
        return { key: "quotes", load: () => import("./Quotes.svelte") };
      case "org.quote":
        return { key: "quote-detail", load: () => import("./QuoteDetail.svelte") };
      case "sso":
        return { key: "sso", load: () => import("./Sso.svelte") };
      case "audit":
        return { key: "audit", load: () => import("./Audit.svelte") };
      case "settings":
        return { key: "settings", load: () => import("./Settings.svelte") };
      default:
        return null;
    }
  }
  const page = $derived(view.kind === "app" ? pageFor(view.name) : null);

  let navOpen = $state(false);
  $effect(() => {
    // Close the mobile nav on route change.
    void router.match.path;
    navOpen = false;
  });
</script>

{#snippet navIcon(name: RouteName)}
  <svg class="nav-ico" viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
    {#if name === "overview"}
      <rect x="3" y="3" width="6" height="6" rx="1.4" /><rect x="11" y="3" width="6" height="6" rx="1.4" /><rect x="3" y="11" width="6" height="6" rx="1.4" /><rect x="11" y="11" width="6" height="6" rx="1.4" />
    {:else if name === "org.users"}
      <circle cx="7.5" cy="7" r="2.7" /><path d="M2.8 16.2c0-2.7 2.1-4.3 4.7-4.3s4.7 1.6 4.7 4.3" /><circle cx="14.4" cy="7.8" r="1.9" /><path d="M13.2 12.1c2.2-.3 3.9 1.1 3.9 3.6" />
    {:else if name === "plan.seats"}
      <path d="M3 15.5v-2.2a2.4 2.4 0 0 1 2.4-2.4h3.2a2.4 2.4 0 0 1 2.4 2.4v2.2" /><circle cx="7" cy="6.4" r="2.4" /><path d="M13.4 15.5v-1.9c0-1 .5-1.9 1.3-2.4" /><path d="M14.4 4.9h2.9v3.4h-2.9z" />
    {:else if name === "access.policies"}
      <rect x="4" y="8.6" width="12" height="8" rx="2" /><path d="M7.2 8.6V6.4a2.8 2.8 0 0 1 5.6 0v2.2" /><path d="M10 11.6v2.2" />
    {:else if name === "extension.policy"}
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h7A2.5 2.5 0 0 1 16 6.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 4 13.5v-7z" /><path d="M8 10h4M10 8v4" />
    {:else if name === "billing"}
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" /><path d="M2.5 8.4h15" /><path d="M5.5 12.5h3" />
    {:else if name === "sso"}
      <path d="M10 2.6l5.5 2v4.3c0 3.4-2.2 5.6-5.5 6.7-3.3-1.1-5.5-3.3-5.5-6.7V4.6z" /><path d="M7.8 9.8l1.6 1.6 3-3.2" />
    {:else if name === "audit"}
      <rect x="4" y="2.6" width="12" height="14.8" rx="2" /><path d="M7 6.6h6M7 10h6M7 13.4h4" />
    {:else if name === "settings"}
      <circle cx="7" cy="6.8" r="1.9" /><circle cx="12.8" cy="13.2" r="1.9" /><path d="M9 6.8h7.5M3.5 6.8H5M3.5 13.2h7.4M14.8 13.2h1.7" />
    {/if}
  </svg>
{/snippet}

{#snippet navList()}
  {#if navItems.length > 0}
  <nav aria-label="Primary">
    <p class="nav-eyebrow">Organization</p>
    <ul class="nav">
      {#each navItems as item (item.name)}
        <li>
          <a href={item.href} use:link class="nav-link" class:active={isActive(item.name)} aria-current={isActive(item.name) ? "page" : undefined}>
            {@render navIcon(item.name)}
            <span>{item.label}</span>
          </a>
        </li>
      {/each}
    </ul>
  </nav>
  {/if}
{/snippet}

<div class="shell">
  <aside class="sidebar">
    <a class="brand" href={`/orgs/${orgId}/overview`} use:link>
      <AppLogo size={28} />
      <span>EpiDev</span>
    </a>
    {#if switcherItems.length > 1}
      <div class="org-switcher">
        <span class="org-name">{currentOrg?.name}</span>
        <Menu items={switcherItems} label="Switch organization" align="start" />
      </div>
    {:else}
      <div class="org-switcher single"><span class="org-name">{currentOrg?.name}</span></div>
    {/if}
    {@render navList()}
  </aside>

  <div class="main-col">
    <header class="topbar">
      <button class="hamburger" aria-label="Open navigation" aria-expanded={navOpen} onclick={() => (navOpen = true)}>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
      </button>
      <div class="topbar-title">
        <AppLogo size={24} />
        <span class="wordmark">EpiDev Portal</span>
      </div>
      <div class="topbar-right">
        {#if unlicensedMember}
          <Badge tone="muted" title="Product access">No seat</Badge>
        {:else if org.license}
          <Badge tone="accent" title="Plan">{planBadgeLabel(org.license)}</Badge>
        {/if}
        <AccountMenu items={accountMenuItems} />
      </div>
    </header>

    <main id="main-content" class="content">
      {#if hasFeature(currentOrg, "portal.billing") && view.name !== "org.quotes" && view.name !== "org.quote"}
        {#key orgId}
          <OpenQuoteNotification {orgId} />
        {/key}
      {/if}
      {#if showUpgradeBanner && plans.currentAccessLevel}
        <TierUpgradeBanner
          {orgId}
          currentPlan={plans.currentPlan}
          currentAccessLevel={plans.currentAccessLevel}
          offers={plans.offers}
          licensedSeats={org.license?.licensedSeats ?? null}
          onSnooze={() => (upgradeSnoozed = true)}
        />
      {/if}
      {#if managementOnly && router.match.name !== "unlicensed"}
        <div class="shell-banner">
          {#if unboundOrganization}
            <Banner tone="info" title="This organization is not linked to a customer" role="status">
              You can manage {currentOrg?.name ?? "this organization"}, its members, settings,
              and audit history. Product, plan, billing, seat, SSO, and access-policy features
              remain unavailable until support links an entitlement customer.
            </Banner>
          {:else}
            <Banner tone="info" title="Product access is unlicensed for your account" role="status">
              You manage {currentOrg?.name ?? "this organization"} without holding a seat, so the
              EpiDev product, extension, and APIs are unavailable to you personally. Organization
              management is unaffected — assign yourself one of your available seats to use the
              product.
            </Banner>
          {/if}
        </div>
      {/if}
      {#if view.kind === "denied"}
        <PermissionDenied permission={view.permission} />
      {:else if page}
        {#key page.key}
          {#await loadPage(view.name, page.load)}
            <div class="page-loading" role="status" aria-live="polite"><span class="sr-only">Loading page…</span></div>
          {:then module}
            <module.default {orgId} />
          {:catch}
            <Banner tone="error" title="We couldn't load this page" role="alert">
              Refresh the page and try again.
            </Banner>
          {/await}
        {/key}
      {/if}
    </main>
  </div>
</div>

{#if navOpen}
  <Drawer open={navOpen} title="Navigation" onclose={() => (navOpen = false)}>
    {#if switcherItems.length > 1}
      <div class="drawer-org">
        <span class="org-name">{currentOrg?.name}</span>
        <Menu items={switcherItems} label="Switch organization" align="start" />
      </div>
    {/if}
    {@render navList()}
  </Drawer>
{/if}

<style>
  .shell {
    display: grid;
    grid-template-columns: var(--sidebar-w) 1fr;
    min-height: 100vh;
  }
  .sidebar {
    border-right: 1px solid var(--line-soft);
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    background: var(--sidebar-sheen);
    position: sticky;
    top: 0;
    align-self: start;
    height: 100vh;
    overflow-y: auto;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 11px;
    font-weight: 650;
    font-size: 16px;
    /* The site pins its brand lockup to letter-spacing: 0. */
    letter-spacing: 0;
    color: var(--heading);
    padding: 2px 6px 4px;
  }
  .org-switcher {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 9px 8px 9px 12px;
    background: var(--surface);
    border: 1px solid var(--line-soft);
    border-radius: var(--radius-btn);
    transition: border-color 0.2s, background 0.2s;
  }
  .org-switcher:hover {
    border-color: var(--line);
  }
  .org-switcher.single {
    justify-content: flex-start;
  }
  .org-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .nav-eyebrow {
    margin: 0 0 8px;
    padding: 0 10px;
    color: var(--muted);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }
  .nav {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .nav-link {
    position: relative;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 12px;
    border-radius: var(--radius-btn);
    color: var(--muted);
    font-size: 14px;
    font-weight: 550;
    text-decoration: none;
    min-height: 40px;
    transition: color 0.16s, background 0.16s;
  }
  .nav-ico {
    width: 18px;
    height: 18px;
    flex: none;
    opacity: 0.85;
    transition: color 0.16s, opacity 0.16s;
  }
  /* Accent rail marks the active route; scales in from the leading edge. */
  .nav-link::before {
    content: "";
    position: absolute;
    left: 3px;
    top: 50%;
    height: 18px;
    width: 3px;
    border-radius: 3px;
    background: var(--accent);
    transform: translateY(-50%) scaleY(0);
    transition: transform 0.16s ease;
  }
  .nav-link:hover {
    color: var(--text);
    background: var(--bg-2);
  }
  .nav-link:hover .nav-ico {
    opacity: 1;
  }
  .nav-link.active {
    color: var(--heading);
    background: var(--accent-soft);
  }
  .nav-link.active .nav-ico {
    color: var(--accent);
    opacity: 1;
  }
  .nav-link.active::before {
    transform: translateY(-50%) scaleY(1);
  }
  .main-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .topbar {
    height: var(--topbar-h);
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 26px;
    border-bottom: 1px solid var(--line-soft);
    position: sticky;
    top: 0;
    /* Site glass: the marketing header's own backdrop (--glass) at blur(14px), over
       a --line-soft hairline. Same treatment in the admin console, so the two shells
       read as one chrome. Layout and sticky behaviour are unchanged — this is a
       paint swap. */
    background: var(--glass);
    backdrop-filter: saturate(140%) blur(14px);
    -webkit-backdrop-filter: saturate(140%) blur(14px);
    z-index: 10;
  }
  .hamburger {
    display: none;
    width: 38px;
    height: 38px;
    place-items: center;
    border: 1px solid var(--line-soft);
    border-radius: 8px;
    background: transparent;
    color: var(--text);
    cursor: pointer;
  }
  .hamburger svg {
    width: 20px;
    height: 20px;
  }
  .topbar-title {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .wordmark {
    font-weight: 650;
    letter-spacing: 0;
    color: var(--heading);
  }
  .topbar-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .content {
    padding: 30px 26px 72px;
    max-width: var(--maxw-app);
    width: 100%;
    margin: 0 auto;
    flex: 1 1 auto;
  }
  .page-loading {
    min-height: 40vh;
  }
  .shell-banner {
    margin-bottom: 18px;
  }
  .drawer-org {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  @media (max-width: 1080px) {
    .shell {
      grid-template-columns: 1fr;
    }
    .sidebar {
      display: none;
    }
    .hamburger {
      display: grid;
    }
  }
  @media (max-width: 560px) {
    .wordmark {
      display: none;
    }
    .content {
      padding: 18px 14px 48px;
    }
  }
</style>
